/**
 * ChartCard — reusable wrapper for a dashboard chart with two toolbar actions:
 *   1. Expand (⛶) — opens a fullscreen dialog with the same chart at max size
 *   2. Export XLSX — downloads a spreadsheet from `exportUrl`
 *
 * The chart itself is passed as a render prop (`renderChart(height)`) so the
 * same code paths work for the card view (fixed height) and the expanded
 * dialog (viewport-sized). Iter 64.
 */
import { useState } from "react";
import { Maximize2, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ChartCard({
  title,
  subtitle,
  renderChart,
  exportUrl,
  exportParams,
  exportFilename = "export.xlsx",
  className = "",
  testid,
  action,          // optional extra element rendered next to Expand/Export
  lang = "es",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (!exportUrl) return;
    setBusy(true);
    try {
      const res = await api.get(exportUrl, {
        params: exportParams || {},
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Export failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={testid}
      className={`bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6 card-lift ${className}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
          {subtitle && (
            <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {action}
          {exportUrl && (
            <Button
              size="sm"
              variant="outline"
              onClick={download}
              disabled={disabled || busy}
              className="h-8 px-2"
              title={lang === "es" ? "Descargar Excel" : "Download Excel"}
              data-testid={testid ? `${testid}-export` : undefined}
            >
              <FileSpreadsheet size={13} />
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpen(true)}
            disabled={disabled}
            className="h-8 px-2"
            title={lang === "es" ? "Ampliar" : "Expand"}
            data-testid={testid ? `${testid}-expand` : undefined}
          >
            <Maximize2 size={13} />
          </Button>
        </div>
      </div>
      {renderChart(280)}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="!max-w-[95vw] w-[95vw] h-[92vh] p-6 flex flex-col"
          data-testid={testid ? `${testid}-dialog` : undefined}
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-3">
              {title}
              {subtitle && (
                <span className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-sans font-normal">
                  {subtitle}
                </span>
              )}
              {exportUrl && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={download}
                  disabled={busy}
                  className="ml-auto"
                  data-testid={testid ? `${testid}-dialog-export` : undefined}
                >
                  <FileSpreadsheet size={13} className="mr-1.5" />
                  {lang === "es" ? "Excel" : "Excel"}
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            {/* Recharts inside the dialog uses viewport height so it truly
                spans the modal. */}
            {renderChart("100%")}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
